import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Home, Image as ImageIcon, X, Heart, Package, ChevronRight, PanelLeft, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { flattenFabrics } from './flattenFabrics';
import { FabricSwatchCard } from './FabricSwatchCard';
import { FabricDetailPanel } from './FabricDetailPanel';
import { FabricShortlistView } from './FabricShortlistView';
import { FabricLightbox } from './FabricLightbox';
import { FabricTreeNav } from './FabricTreeNav';
import { useFabricFavorites } from '../../../hooks/useFabricFavorites';
import { addSampleColor, getSampleColors, isInSampleRequest, removeSampleColor } from '../../../lib/sampleRequest';
import { ROOM_TYPES, DEFAULT_ROOM_TYPE } from '../../../lib/roomTypes';
import { addRecentlyViewed } from '../../../lib/recentlyViewed';
import { isUnavailable } from '../../../lib/fabricLabels';
import { RecentlyViewedStrip } from '../../fabric/RecentlyViewedStrip';
import { brandLabel } from '../../../lib/brandNames';

const SCOPE_KEYS = ['brand', 'collection', 'material'];
const scopeFromParams = (params) => Object.fromEntries(SCOPE_KEYS.filter((k) => params.get(k)).map((k) => [k, params.get(k)]));

export const FabricSwatchGallery = ({ brands, selectedColorId, onSelect, selectLabel, className = '' }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sharedIds = useMemo(() => (searchParams.get('shortlist') || '').split(',').filter(Boolean), [searchParams]);
  const entries = useMemo(() => flattenFabrics(brands), [brands]);
  const sharedEntries = useMemo(() => entries.filter((e) => sharedIds.includes(e.id)), [entries, sharedIds]);
  const [search, setSearch] = useState(() => searchParams.get('q') || '');
  const [inStockOnly, setInStockOnly] = useState(() => searchParams.get('stock') === 'in');
  const [scope, setScopeState] = useState(() => scopeFromParams(searchParams));
  const [navOpen, setNavOpen] = useState(true);
  const [view, setView] = useState('swatch');
  const [active, setActive] = useState(null);
  const [showShortlist, setShowShortlist] = useState(sharedIds.length > 0);
  const [roomType, setRoomType] = useState(DEFAULT_ROOM_TYPE);
  const [lightbox, setLightbox] = useState(null);
  const [sampleCount, setSampleCount] = useState(() => getSampleColors().length);
  const { favorites, isFavorite, toggleFavorite } = useFabricFavorites();

  useEffect(() => {
    const sync = () => setSampleCount(getSampleColors().length);
    window.addEventListener('sampleRequestColors:change', sync);
    return () => window.removeEventListener('sampleRequestColors:change', sync);
  }, []);

  // Deep links: keep brand/collection/material/q in the URL so the filtered view is shareable.
  const setScope = setScopeState;
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    SCOPE_KEYS.forEach((k) => (scope[k] ? params.set(k, scope[k]) : params.delete(k)));
    const q = search.trim();
    q ? params.set('q', q) : params.delete('q');
    inStockOnly ? params.set('stock', 'in') : params.delete('stock');
    if (!pendingFabric.current) params.delete('fabric');
    if (params.toString() !== searchParams.toString()) setSearchParams(params, { replace: true });
  }, [scope, search, inStockOnly]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fromUrl = scopeFromParams(searchParams);
    if (JSON.stringify(fromUrl) !== JSON.stringify(scope)) setScopeState(fromUrl);
    const q = searchParams.get('q') || '';
    if (q !== search.trim()) setSearch(q);
    const stock = searchParams.get('stock') === 'in';
    if (stock !== inStockOnly) setInStockOnly(stock);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const shareView = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link to this view copied');
    } catch {
      toast.info(window.location.href);
    }
  };

  const openLightbox = (entry) => { if (entry) { addRecentlyViewed(entry); setLightbox(entry); } };
  // Shareable swatch link: ?fabric=<id> opens that swatch enlarged once entries are loaded.
  const pendingFabric = useRef(searchParams.get('fabric'));
  useEffect(() => {
    const fabricId = pendingFabric.current;
    if (!fabricId || !entries.length) return;
    pendingFabric.current = null;
    const entry = entries.find((e) => e.id === fabricId);
    if (entry) {
      setScopeState({ brand: entry.brand_name, collection: entry.collection_name, ...(entry.material_name ? { material: entry.material_name } : {}) });
      openLightbox(entry);
    } else {
      toast.error('That fabric is no longer available');
      const params = new URLSearchParams(searchParams);
      params.delete('fabric');
      setSearchParams(params, { replace: true });
    }
  }, [entries]); // eslint-disable-line react-hooks/exhaustive-deps
  const openRecent = (item) => openLightbox(entries.find((e) => e.id === item.id) || item);

  const scoped = useMemo(
    () => entries.filter((e) =>
      (!scope.brand || e.brand_name === scope.brand) &&
      (!scope.collection || e.collection_name === scope.collection) &&
      (!scope.material || e.material_name === scope.material)),
    [entries, scope]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scoped.filter((e) =>
      (!inStockOnly || !isUnavailable(e)) &&
      (!q || [e.name, e.code, e.collection_name, e.brand_name, e.material_name].some((v) => v?.toLowerCase().includes(q))));
  }, [scoped, search, inStockOnly]);
  const unavailableCount = useMemo(() => scoped.filter(isUnavailable).length, [scoped]);

  const hasFilters = search || scope.brand || inStockOnly;
  const clear = () => { setSearch(''); setScope({}); setInStockOnly(false); };
  const crumbs = [
    { label: 'All fabrics', scope: {} },
    scope.brand && { label: brandLabel(scope.brand), scope: { brand: scope.brand } },
    scope.collection && { label: scope.collection, scope: { brand: scope.brand, collection: scope.collection } },
    scope.material && { label: scope.material, scope },
  ].filter(Boolean);

  const handleToggleFavorite = (entry) => {
    const added = toggleFavorite(entry);
    toast[added ? 'success' : 'info'](added ? `${entry.name} added to your shortlist` : `${entry.name} removed from shortlist`);
  };

  const closeShared = () => {
    if (sharedIds.length) {
      searchParams.delete('shortlist');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const saveShared = () => {
    sharedEntries.forEach((e) => { if (!isFavorite(e.id)) toggleFavorite(e); });
    toast.success(`${sharedEntries.length} fabrics saved to your shortlist`);
    closeShared();
  };

  const handleRequestSample = (entry) => {
    if (isInSampleRequest(entry.id)) {
      setSampleCount(removeSampleColor(entry.id));
      toast.info(`${entry.name} removed from sample request`);
      return;
    }
    const count = addSampleColor(entry);
    setSampleCount(count);
    toast.success(`${entry.name} added to sample request`, {
      action: { label: `Review (${count})`, onClick: () => navigate('/request-samples') },
    });
  };

  return (
    <div className={`flex flex-col lg:flex-row gap-0 min-h-0 ${className}`} data-testid="fabric-gallery-root">
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        <div className="space-y-3 pb-4 border-b">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, code or collection…" className="pl-9" data-testid="fabric-gallery-search" />
            </div>
            <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
              <Button size="sm" variant={view === 'swatch' ? 'default' : 'ghost'} className="h-8 gap-1.5" onClick={() => setView('swatch')} data-testid="fabric-gallery-view-swatch">
                <ImageIcon className="w-3.5 h-3.5" /> Swatch
              </Button>
              <Button size="sm" variant={view === 'room' ? 'default' : 'ghost'} className="h-8 gap-1.5" onClick={() => setView('room')} data-testid="fabric-gallery-view-room">
                <Home className="w-3.5 h-3.5" /> Room
              </Button>
            </div>
            <Button
              size="sm"
              variant={showShortlist ? 'default' : 'outline'}
              onClick={() => setShowShortlist((s) => !s)}
              className={`h-8 gap-1.5 ${showShortlist ? 'bg-rose-500 hover:bg-rose-600 border-rose-500' : 'border-rose-200 text-rose-600 hover:bg-rose-50'}`}
              data-testid="fabric-shortlist-btn"
            >
              <Heart className={`w-3.5 h-3.5 ${favorites.length ? 'fill-current' : ''}`} /> Shortlist ({favorites.length})
            </Button>
            {sampleCount > 0 && (
              <Button size="sm" variant="outline" onClick={() => navigate('/request-samples')} className="h-8 gap-1.5 border-green-300 text-green-700 hover:bg-green-50" data-testid="fabric-sample-review-btn">
                <Package className="w-3.5 h-3.5" /> Review request ({sampleCount})
              </Button>
            )}
            {hasFilters && !showShortlist && (
              <Button variant="ghost" size="sm" onClick={clear} className="gap-1 text-neutral-500" data-testid="fabric-gallery-clear">
                <X className="w-4 h-4" /> Clear
              </Button>
            )}
          </div>
          {(view === 'room' || showShortlist) && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="fabric-room-picker">
              <span className="text-[11px] uppercase tracking-wider text-neutral-400 w-16 shrink-0">Room</span>
              {ROOM_TYPES.map((room) => (
                <button
                  key={room.id}
                  type="button"
                  onClick={() => setRoomType(room.id)}
                  data-testid={`fabric-room-${room.id}`}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${roomType === room.id ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-200 hover:border-neutral-400'}`}
                >
                  {room.label}
                </button>
              ))}
            </div>
          )}
          {!showShortlist && (
            <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="fabric-breadcrumb">
              <button onClick={() => setNavOpen((v) => !v)} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-neutral-200 text-neutral-600 hover:border-amber-400 lg:hidden" data-testid="fabric-nav-toggle">
                <PanelLeft className="w-3.5 h-3.5" /> Browse
              </button>
              {crumbs.map((c, i) => (
                <React.Fragment key={c.label}>
                  {i > 0 && <ChevronRight className="w-3 h-3 text-neutral-300" />}
                  <button onClick={() => setScope(c.scope)} className={`${i === crumbs.length - 1 ? 'font-semibold text-neutral-900' : 'text-neutral-500 hover:text-amber-700'}`} data-testid={`fabric-crumb-${i}`}>{c.label}</button>
                </React.Fragment>
              ))}
              <button
                type="button"
                onClick={() => setInStockOnly((v) => !v)}
                aria-pressed={inStockOnly}
                className={`ml-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors ${inStockOnly ? 'bg-emerald-600 text-white border-emerald-600' : 'border-neutral-200 text-neutral-600 hover:border-emerald-400 hover:text-emerald-700'}`}
                title={inStockOnly ? 'Showing available fabrics only' : 'Hide fabrics that are out of stock'}
                data-testid="fabric-in-stock-chip"
              >
                <span className={`w-2 h-2 rounded-full ${inStockOnly ? 'bg-white' : 'bg-emerald-500'}`} />
                In stock only{!inStockOnly && unavailableCount > 0 ? <span className="text-neutral-400">({unavailableCount} hidden)</span> : null}
              </button>
              <span className="ml-auto text-neutral-500" data-testid="fabric-gallery-count">{filtered.length} of {entries.length} fabrics</span>
              <button onClick={shareView} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-neutral-200 text-neutral-600 hover:border-amber-400 hover:text-amber-700" title="Copy a link to this filtered view" data-testid="fabric-share-view-btn">
                <Link2 className="w-3.5 h-3.5" /> Share view
              </button>
            </div>
          )}
          {!showShortlist && <RecentlyViewedStrip onSelect={openRecent} />}
        </div>

        <div className="flex-1 flex gap-4 pt-4 min-h-0">
          {!showShortlist && navOpen && (
            <div className="w-full lg:w-72 xl:w-80 shrink-0 lg:sticky lg:top-24 self-start max-h-[75vh] overflow-y-auto" data-testid="fabric-tree-wrap">
              <FabricTreeNav entries={entries} scope={scope} onScope={(sc) => { setScope(sc); if (window.innerWidth < 1024) setNavOpen(false); }} />
            </div>
          )}
          <div className={`flex-1 min-w-0 overflow-y-auto pr-1 ${!showShortlist && navOpen ? 'hidden lg:block' : ''}`}>
          {showShortlist ? (
            <FabricShortlistView
              favorites={sharedIds.length ? sharedEntries : favorites}
              shared={sharedIds.length > 0}
              onSaveShared={saveShared}
              onRemove={handleToggleFavorite}
              onSelect={onSelect}
              selectLabel={selectLabel || 'Select this fabric'}
              roomType={roomType}
              onBack={() => { setShowShortlist(false); closeShared(); }}
            />
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-neutral-500" data-testid="fabric-gallery-empty">No fabrics match your filters.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="fabric-gallery-grid">
              {filtered.map((entry) => (
                <FabricSwatchCard
                  key={entry.key || entry.id}
                  entry={entry}
                  view={view}
                  roomType={roomType}
                  isSelected={entry.id === selectedColorId}
                  isActive={(entry.key || entry.id) === (active?.key || active?.id)}
                  isFavorite={isFavorite(entry.id)}
                  onToggleFavorite={handleToggleFavorite}
                  onClick={setActive}
                  onEnlarge={openLightbox}
                />
              ))}
            </div>
          )}
          </div>
        </div>
      </div>

      {active && !showShortlist && (
        <aside className="lg:w-80 xl:w-96 shrink-0 mt-4 lg:mt-0 lg:ml-4 rounded-xl lg:rounded-none border lg:border-y-0 lg:border-r-0 overflow-hidden min-h-0">
          <FabricDetailPanel
            key={active.id}
            entry={active}
            isSelected={active.id === selectedColorId}
            onSelect={onSelect}
            selectLabel={selectLabel}
            onClose={() => setActive(null)}
            isFavorite={isFavorite(active.id)}
            onToggleFavorite={handleToggleFavorite}
            inSampleRequest={isInSampleRequest(active.id) && sampleCount >= 0}
            onRequestSample={handleRequestSample}
            onEnlarge={openLightbox}
            roomType={roomType}
            allEntries={entries}
            onPickAlternative={(e) => { setActive(e); addRecentlyViewed(e); }}
            onRoomTypeChange={setRoomType}
          />
        </aside>
      )}

      <FabricLightbox entry={lightbox} open={!!lightbox} onOpenChange={(open) => !open && setLightbox(null)} />
    </div>
  );
};

export default FabricSwatchGallery;
